import { create } from 'zustand';

/**
 * El tema, con las tres opciones que la gente espera: claro, oscuro y «lo que
 * diga el sistema».
 *
 * `13 §1` daba el tema por hecho como estado de Zustand y nunca se implementó:
 * la interfaz seguía a `prefers-color-scheme` sin que nadie pudiera llevarle la
 * contraria. Aquí «sistema» es un valor de primera clase y no la ausencia de
 * elección, porque es la única forma de que quien conmuta a mano pueda volver.
 *
 * **El producto es oscuro por defecto**, y eso es una decisión de producto y no
 * el reflejo del sistema operativo: sin nada guardado se arranca en oscuro
 * aunque el escritorio esté en claro. «Sistema» sigue estando, pero hay que
 * pedirlo — es una elección, no el hueco que queda cuando no hay ninguna.
 *
 * El valor efectivo se escribe en `data-theme` del elemento raíz, que es de
 * donde `index.css` cuelga la paleta oscura. El primer pintado ya lo ha hecho
 * el script en línea de `index.html` —si no, la aplicación destellaría antes de
 * que React arrancase—; esto se limita a mantenerlo. Los dos sitios tienen que
 * caer al mismo valor por defecto o el destello vuelve.
 */
export type ThemeChoice = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'absor.theme';

/** Lo que se ve sin haber elegido nada. Replicado en el script de `index.html`. */
const FALLBACK: ThemeChoice = 'dark';

function isChoice(value: unknown): value is ThemeChoice {
  return value === 'light' || value === 'dark' || value === 'system';
}

function readStored(): ThemeChoice {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);

    return isChoice(stored) ? stored : FALLBACK;
  } catch {
    return FALLBACK;
  }
}

function systemPrefersDark(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function resolveTheme(choice: ThemeChoice): 'light' | 'dark' {
  return choice === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : choice;
}

function apply(choice: ThemeChoice): 'light' | 'dark' {
  const resolved = resolveTheme(choice);

  document.documentElement.dataset['theme'] = resolved;
  document.documentElement.style.colorScheme = resolved;

  // La barra del navegador en móvil pinta este color detrás del contenido; sin
  // actualizarlo, el tema oscuro queda con una franja blanca arriba.
  //
  // Los dos valores son `--color-canvas` de cada tema, escritos a mano aquí y
  // en `index.html`. Duplicarlos es feo pero inevitable: el script de arranque
  // corre antes de que exista hoja de estilo alguna que consultar, así que no
  // hay variable CSS que leer. Si `--color-canvas` cambia, cambian los tres.
  const meta = document.querySelector('meta[name="theme-color"]');

  if (meta instanceof HTMLMetaElement) {
    meta.content = resolved === 'dark' ? '#08080d' : '#f6f7fb';
  }

  return resolved;
}

interface ThemeState {
  choice: ThemeChoice;
  resolved: 'light' | 'dark';
  setChoice: (choice: ThemeChoice) => void;
  /** Lo que llama el interruptor de la cabecera: alterna el tema que se ve. */
  toggle: () => void;
}

export const useTheme = create<ThemeState>((set, get) => ({
  choice: readStored(),
  resolved: resolveTheme(readStored()),

  setChoice: (choice) => {
    try {
      localStorage.setItem(STORAGE_KEY, choice);
    } catch {
      /* Almacenamiento bloqueado: el tema vale para esta sesión y ya. */
    }

    set({ choice, resolved: apply(choice) });
  },

  toggle: () => {
    get().setChoice(get().resolved === 'dark' ? 'light' : 'dark');
  },
}));

/**
 * Mientras la elección sea «sistema», seguir al sistema **también cuando
 * cambia**: quien tiene el escritorio en automático al anochecer espera que la
 * pestaña abierta cambie con él, no en la próxima recarga.
 */
export function watchSystemTheme(): () => void {
  const media = window.matchMedia('(prefers-color-scheme: dark)');
  const onChange = () => {
    if (useTheme.getState().choice === 'system') {
      useTheme.setState({ resolved: apply('system') });
    }
  };

  media.addEventListener('change', onChange);

  return () => media.removeEventListener('change', onChange);
}
